import { useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Phone, Mail, MapPin, Clock, Send, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PHONE_NUMBER = "306-262-5995";
const EMAIL = "306LOCKSMITH@GMAIL.COM";

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    service_type: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData((prev) => ({ ...prev, service_type: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      toast.success("Message sent! We'll contact you shortly.");
    } catch (error) {
      toast.error("Failed to submit. Please call us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900">
      <Header />
      
      {/* Hero */}
      <section className="pt-32 pb-20 px-4 md:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white mb-6">
            Contact <span className="text-amber-400">Us</span>
          </h1>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            Have a question or need service? Reach out to us anytime - we're here to help!
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="pb-20 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="font-heading text-2xl text-white mb-8">Get In Touch</h2>
              
              <div className="space-y-6 mb-10">
                <a href={`tel:${PHONE_NUMBER.replace(/-/g, "")}`} className="flex items-start gap-4 group">
                  <div className="w-14 h-14 bg-amber-500 rounded-sm flex items-center justify-center flex-shrink-0">
                    <Phone className="w-7 h-7 text-slate-900" />
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm">Call Us (24/7)</p>
                    <p className="text-amber-400 font-bold text-2xl group-hover:text-amber-300 transition-colors">{PHONE_NUMBER}</p>
                  </div>
                </a>

                <a href={`mailto:${EMAIL}`} className="flex items-start gap-4 group">
                  <div className="w-14 h-14 bg-slate-800 border border-slate-700 rounded-sm flex items-center justify-center flex-shrink-0">
                    <Mail className="w-7 h-7 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm">Email Us</p>
                    <p className="text-white font-semibold text-lg group-hover:text-amber-400 transition-colors">{EMAIL}</p>
                  </div>
                </a>

                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-slate-800 border border-slate-700 rounded-sm flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-7 h-7 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm">Service Area</p>
                    <p className="text-white font-semibold text-lg">Saskatoon & Surrounding Areas</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-slate-800 border border-slate-700 rounded-sm flex items-center justify-center flex-shrink-0">
                    <Clock className="w-7 h-7 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm">Hours</p>
                    <p className="text-white font-semibold text-lg">24 Hours / 7 Days a Week</p>
                  </div>
                </div>
              </div>

              {/* Emergency CTA */}
              <div className="bg-amber-500 rounded-sm p-6">
                <p className="text-slate-900 font-bold text-lg mb-2">Emergency? Don't wait!</p>
                <a href={`tel:${PHONE_NUMBER.replace(/-/g, "")}`} className="flex items-center gap-2 text-slate-900 font-heading text-2xl hover:text-blue-900 transition-colors">
                  <Phone className="w-6 h-6" />
                  Call {PHONE_NUMBER} Now
                </a>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-slate-800 border border-slate-700 rounded-sm p-8">
              {isSubmitted ? (
                <div className="h-full flex flex-col items-center justify-center text-center py-12">
                  <CheckCircle className="w-16 h-16 text-green-500 mb-4" />
                  <h3 className="font-heading text-2xl text-white mb-2">Message Sent!</h3>
                  <p className="text-slate-400">We'll get back to you shortly.</p>
                </div>
              ) : (
                <>
                  <h2 className="font-heading text-2xl text-white mb-2">Send Us a Message</h2>
                  <p className="text-slate-400 mb-6">Fill out the form and we'll respond promptly.</p>

                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div>
                      <Label htmlFor="name" className="text-slate-300">Your Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="John Smith"
                        required
                        className="mt-1 bg-slate-700 border-slate-600 text-white"
                      />
                    </div>

                    <div>
                      <Label htmlFor="phone" className="text-slate-300">Phone Number *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="306-555-1234"
                        required
                        className="mt-1 bg-slate-700 border-slate-600 text-white"
                      />
                    </div>

                    <div>
                      <Label htmlFor="email" className="text-slate-300">Email (Optional)</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="john@example.com"
                        className="mt-1 bg-slate-700 border-slate-600 text-white"
                      />
                    </div>

                    <div>
                      <Label htmlFor="service" className="text-slate-300">Service Type *</Label>
                      <Select value={formData.service_type} onValueChange={handleServiceChange} required>
                        <SelectTrigger className="mt-1 bg-slate-700 border-slate-600 text-white">
                          <SelectValue placeholder="Select a service" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="residential">Residential Locksmith</SelectItem>
                          <SelectItem value="commercial">Commercial Locksmith</SelectItem>
                          <SelectItem value="automotive">Car Locksmith</SelectItem>
                          <SelectItem value="emergency">Emergency Lockout</SelectItem>
                          <SelectItem value="other">Other Service</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="message" className="text-slate-300">Message *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Tell us about your situation..."
                        required
                        rows={4}
                        className="mt-1 bg-slate-700 border-slate-600 text-white resize-none"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold py-6"
                    >
                      {isSubmitting ? "Sending..." : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Send Message
                        </>
                      )}
                    </Button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
